import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { Redirect } from "react-router-dom";
import { useAlert } from "react-alert";
import { Button, Typography } from "@material-ui/core";
import { useHistory, useLocation } from "react-router";
import queryString from "query-string";
import * as Sentry from "@sentry/browser";

import { makeRequest } from "./util";
import { signout } from "./NavBar";
import Topbar from "./Topbar.js";

// eslint-disable-next-line no-unused-vars
const [isValid, wrongEmail, inviteExpired, inviteAlreadyAccepted] = [
  0,
  1,
  2,
  3,
];

function AcceptInvitePage() {
  const location = useLocation();
  const alert = useAlert();

  const [transferTeamsConfirmed, setTransferTeamsConfirmed] = useState(false);
  const [needConfirmation, setNeedConfirmation] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [invalidInviteReason, setInvalidInviteReason] = useState(null);

  const inviteID = queryString.parse(location.search).id;

  useEffect(() => {
    makeRequest("accept_invite", {
      invite_id: inviteID,
      confirmed: transferTeamsConfirmed,
    })
      .then((response) => {
        if (response.data.result === "success") {
          setInviteSuccess(true);
        } else if (response.data.result === "invalid") {
          setInvalidInviteReason(response.data.invalid_reason);
        } else if (response.data.result === "needs_confirmation") {
          setNeedConfirmation(true);
        }
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to load page.");
        console.log(error);
        Sentry.captureException(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferTeamsConfirmed, inviteID]);

  if (inviteSuccess) {
    return <Redirect to={"/events"} />;
  } else if (needConfirmation) {
    return (
      <ConfirmSwitchTeams onConfirm={() => setTransferTeamsConfirmed(true)} />
    );
  } else if (invalidInviteReason) {
    return <InvalidInvitePage reason={invalidInviteReason} />;
  } else {
    return null;
  }
}

const useSwitchTeamsStyles = makeStyles(() => ({
  content: {
    paddingTop: 175,
    textAlign: "center",
  },
  header: {
    marginTop: 55,
  },
  paragraph: {
    textAlign: "center",
    display: "inline-block",
    width: 455,
    marginTop: 40,
  },
  subHeader: {
    textAlign: "center",
    display: "inline-block",
    width: 455,
    marginTop: 30,
  },
  buttons: {
    marginTop: 25,
  },
  spacing: {
    display: "inline-block",
    width: 60,
  },
}));

function ConfirmSwitchTeams({ onConfirm }) {
  const classes = useSwitchTeamsStyles();
  const history = useHistory();

  return (
    <>
      <Topbar />
      <div className={clsx(classes.content, "flex flex-col items-center")}>
        <img alt="" src={require("./img/invite_confirm_switch_teams.png")} />
        <Typography className={classes.header} component="h1" variant="h1">
          Join Organization
        </Typography>
        <Typography className={clsx(classes.paragraph)} component="p">
          You will be added as a user to your organizations account. Any data in
          your existing account will be lost. Are you sure you would like to
          proceed?
        </Typography>
        <div className={classes.buttons}>
          <Button
            size="large"
            variant="outlined"
            onClick={() => history.push("/events")}
          >
            Cancel
          </Button>
          <div className={classes.spacing} />
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={onConfirm}
          >
            Accept Invite
          </Button>
        </div>
      </div>
    </>
  );
}

ConfirmSwitchTeams.propTypes = {
  onConfirm: PropTypes.func.isRequired,
};

function InvalidInvitePage({ reason }) {
  const classes = useSwitchTeamsStyles();

  var reasonString;

  if (reason === wrongEmail) {
    reasonString =
      "Reason: You are signed in with a different email than the one the invite was sent to.";
  } else if (reason === inviteExpired) {
    reasonString = "Reason: The invite has expired.";
  } else if (reason === inviteAlreadyAccepted) {
    reasonString = "Reason: The invite has already been accepted";
  } else {
    reasonString = "Please contact support@freshpaint.io";
  }

  return (
    <>
      <Topbar />
      <div className={clsx(classes.content, "flex flex-col items-center")}>
        <img alt="" src={require("./img/invite_is_invalid.png")} />
        <Typography className={classes.header} component="h1" variant="h1">
          Error: Invalid Invite
        </Typography>
        <Typography className={clsx(classes.subHeader)} component="p">
          {reasonString}
        </Typography>
        <div className={classes.buttons}>
          {reason === wrongEmail && (
            <>
              <Button size="large" variant="outlined" onClick={() => signout()}>
                Switch Email
              </Button>
              <div className={classes.spacing} />
            </>
          )}
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={() => (window.location.href = "https://freshpaint.io")}
          >
            Back to Home
          </Button>
        </div>
      </div>
    </>
  );
}

InvalidInvitePage.propTypes = {
  reason: PropTypes.number,
};

export default AcceptInvitePage;
